import { ISearchRailwaysResponse } from '../../../node/repositories/geo/entities/Railways/SearchRailwaysResponse';
import { IBuilderSuggest } from '../../types/builder';
import { IRailway } from '../../types/geo';
import { IThunkActionCreator } from '../../types/store';
import { trackSubmitClicked } from '../../components/commute_popup/tracking';
import { trackRailwaySearchEmpty } from '../../components/stations/tracking';
import { DEFAULT_STATION_DISTANCE, SavedFiltersIds, STATION_SAVED_FILTERS } from '../../interfaces/filters';
import { isArrayWithItems } from '../../utils';
import { trackGeoSuggestItemClick } from '../../utils/analytics';
import { getStationDistanceLabel } from '../../utils/filters_helpers';
import { getBuilderFilterLabel } from '../../utils/getBuilderFilterLabel';
import { DEFAULT_STATION_GROUPS, getStationModels } from '../../utils/stations';
import { showCommutePopup } from '../commute_popup/actions';
import { addBuilderIdFilter, changePage, changeStationDistance } from '../filters/actions';
import { addSavedFilter, justRemoveSavedFilter } from '../saved_filters/actions';
import { removeSavedFilter } from '../saved_filters/removeSavedFilter';
import { filterStations } from './filterStations';
import { onFiltersChanged } from './onFiltersChanged';
import { selectStations } from './selectStations';

export enum ActionTypes {
  Fetched = 'StationsFetched',
  PopupOpened = 'StationsPopupOpened',
  DirectionChanged = 'StationsDirectionChanged',
  SelectedChanged = 'StationsPopupSelectedStationsChanged',
  PopupDistanceChanged = 'StationsPopupDistanceChanged',
  StationsFiltered = 'StationsFiltered',
}

export interface IStationsFetched {
  type: ActionTypes.Fetched;
  value: ISearchRailwaysResponse;
}

export interface IStationsPopupOpened {
  type: ActionTypes.PopupOpened;
  opened: boolean;
}

export interface IStationsPopupDistanceChanged {
  type: ActionTypes.PopupDistanceChanged;
  value: number;
}

export interface IStationsPopupSelectedStationsChanged {
  type: ActionTypes.SelectedChanged;
  value: number[];
}

export const changePopupStationDistance = (value: number): IStationsPopupDistanceChanged => ({
  type: ActionTypes.PopupDistanceChanged,
  value,
});

export const changePopupSelectedStations = (value: number[]): IStationsPopupSelectedStationsChanged => ({
  type: ActionTypes.SelectedChanged,
  value,
});

export function changeStationsSavedFilter(): IThunkActionCreator {
  return (dispatch, getState) => {
    const {
      filters: { station: stationFromFilter, stationDistance },
      stations: { currentStations, directions },
      savedFilters: { items },
    } = getState();
    const station = stationFromFilter || [];

    dispatch(onFiltersChanged(stationDistance, station));

    // Добавляем в сохраненные фильтры дропдаун расстояния от станций
    if (stationDistance) {
      dispatch(
        addSavedFilter({
          id: SavedFiltersIds.StationDistance,
          name: getStationDistanceLabel(stationDistance),
          value: stationDistance,
        }),
      );
    }

    // Удаляем все сохранённые фильтры по станциям
    items.forEach(value => STATION_SAVED_FILTERS.includes(value.id) && dispatch(justRemoveSavedFilter(value)));

    /**
     * Далее идет логика схлопа станций в одного червяка. Делаем это по двум признакам: У станций должно быть одинаковое
     *  направление (Рижское направление) и одна и та же группа дальности (станции сгруппированы по дальности 40-70 км).
     */
    // Проходимся по всем станциям с бэка
    currentStations.forEach(({ id, directions: directionIds, distanceGroup, name }) => {
      // Если итерируемая станция с бэка есть в фильтрах
      if (station.includes(id)) {
        let savedInGroup = false;

        // Заходим в массив направлений этой станции
        directionIds.forEach(directionId => {
          if (!distanceGroup) {
            return;
          }
          const stationIds: number[] = currentStations
            .filter(currentStation => {
              // Фильтруем, чтоб у станций с бэка был одинаковые группа дальности и направление
              return currentStation.distanceGroup === distanceGroup && currentStation.directions.includes(directionId);
            })
            .map(item => item.id);

          // Проверяем по станциям из фильтров, что вся группа чекнута юзером и можно их схлопнуть
          const shouldConcatStations =
            stationIds.length > 0 && stationIds.every(stationId => station.includes(stationId));

          if (shouldConcatStations) {
            savedInGroup = true;
            const compressedStation = directions.find(d => d.id === directionId);

            const stationLabel = `${compressedStation && compressedStation.name} · ${
              DEFAULT_STATION_GROUPS[distanceGroup]
            }`;

            // В value лежит массив айдишников схлопнутых станций, чтобы потом легче удалять их при нажатии на крестик
            dispatch(
              addSavedFilter({
                id: SavedFiltersIds.GroupedStation,
                name: stationLabel,
                value: stationIds,
              }),
            );
          }
        });

        // Если станцию не нужно схлапывать
        if (!savedInGroup) {
          dispatch(
            addSavedFilter({
              id: SavedFiltersIds.Station,
              name,
              value: id,
            }),
          );
        }
      }
    });
  };
}

export interface IStationsPopupDirectionChanged {
  type: ActionTypes.DirectionChanged;
  value: number;
}

export const onChangeStationDirection = (value: number): IStationsPopupDirectionChanged => ({
  type: ActionTypes.DirectionChanged,
  value,
});

export const onChangeStationDistance = (value = DEFAULT_STATION_DISTANCE, save = true): IThunkActionCreator => {
  return dispatch => {
    dispatch(
      addSavedFilter({
        id: SavedFiltersIds.StationDistance,
        name: getStationDistanceLabel(value),
        value,
      }),
    );
    dispatch(changeStationDistance(value));
    if (save) {
      dispatch(changePage(1));
    }
  };
};

export function selectBuilder(builder: IBuilderSuggest): IThunkActionCreator {
  return (dispatch, getState) => {
    if (!builder.id) {
      return;
    }

    const {
      savedFilters: { items },
    } = getState();

    const currentFilter = items.find(({ id }) => id === SavedFiltersIds.BuilderId);
    if (currentFilter) {
      dispatch(removeSavedFilter(currentFilter));
    }

    const { name, id } = builder;
    trackGeoSuggestItemClick('builder/cian');
    dispatch(
      addSavedFilter({
        id: SavedFiltersIds.BuilderId,
        name: getBuilderFilterLabel(name, id),
        value: id,
      }),
    );
    dispatch(addBuilderIdFilter(id));
    dispatch(changePage(1));
  };
}

export const onStationsPopupAccept = (): IThunkActionCreator => (dispatch, getState) => {
  const { stations } = getState();
  const { selectedStations, stationDistance } = stations;
  if (selectedStations.length) {
    trackSubmitClicked('railway_save', selectedStations.length);
  }
  dispatch(showCommutePopup(false));
  dispatch(selectStations(getStationModels(stations), false, stationDistance));
};

export interface IStationsFiltered {
  type: ActionTypes.StationsFiltered;
  stations: IRailway[];
}

export const getStationNameVariantsWithType = (station: IRailway, type: string): string[] => {
  const names = station.alternativeNames ? [station.name].concat(station.alternativeNames) : [station.name];

  return names.reduce((acc, value) => acc.concat(`${type} ${value}`, `${value} ${type}`), [] as string[]);
};

export function filterPopupStations(
  value: string,
  distance: number = DEFAULT_STATION_DISTANCE,
  trackNotFound: boolean = false,
): IThunkActionCreator {
  return (dispatch, getState) => {
    dispatch(filterStations(value, distance));
    if (trackNotFound) {
      const {
        stations: { suggests },
      } = getState();
      if (!isArrayWithItems(suggests)) {
        trackRailwaySearchEmpty();
      }
    }
  };
}

export type Action =
  | IStationsFetched
  | IStationsPopupDirectionChanged
  | IStationsPopupOpened
  | IStationsPopupDistanceChanged
  | IStationsPopupSelectedStationsChanged
  | IStationsFiltered;
